// The old Photohawk blog (2023), restored on its original URLs (/blog/<slug>): one Markdown file per post,
// src/content/blog/<slug>.md, made from the posts' text in git (supabase/migrations/20260324133508_….sql) or, where
// the Wayback Machine holds a fuller copy of the page as it was published, from that. They're served from these files,
// never from the database at runtime.
//
// Unlike the guides, the blog is NOT behind the guides flag: these URLs had links and traffic, so they're live
// everywhere. A post that a pillar guide now covers properly (`supersededBy`) is a 301 to that guide wherever the
// guides are on (src/lib/guidesFlag.ts), and an ordinary post where they're off, so every old URL resolves in one hop.
//
// Front matter: title, metaTitle (the old <title>), description, slug, published (the original date), updated (only
// when an editor's note corrects something), source (where the text came from), supersededBy (a guide slug),
// relatedGuides, editorNote (shown above the post when something in it is no longer right).
//
// New articles (October 2026 onwards, `source: original`) can also set: product (studio | family: which Photohawk
// product the article is about), author (an id from src/content/guides/authors.ts; never a placeholder),
// relatedPosts (other blog slugs, linked under the article everywhere) and faq (question/answer pairs, rendered
// under "Questions people ask" and marked up as FAQPage JSON-LD). Links to pages that are staging-only (the new
// home's /personal, /studio, /download) are rewritten where those pages are off: see src/lib/blogLinks.ts.
//
// Images (October 2026 onwards): heroImage (a content image name, src/content/media), heroAlt (what it shows) and
// heroCaption (optional) put a hero image under the title, eager-loaded; socialImage (a path under the site origin,
// 1200x630 JPEG) and socialAlt set the link-preview image (og:image / twitter:image), which defaults to the card
// scripts/content-images/render.mjs makes from the title and hero (/images/og/<section>/<slug>.jpg) when there's a
// hero. Images in the body are ":::figure <name>" blocks. All of them go in the BlogPosting's ImageObjects and in the
// image sitemap.

import { getGuide, type GuideFaq } from "@/content/guides";
import { findAuthor } from "@/content/guides/authors";
import { splitFrontMatter, type FrontMatter } from "@/content/guides/frontMatter";
import { blocksText, countWords, htmlComments, parseGuideBody, type GuideBlock } from "@/content/guides/body";
import { getMedia } from "@/content/media";

export const BLOG_PATH = "/blog";

/** Which Photohawk product a new article is about (the restored 2023 posts have none). */
export type BlogProduct = "studio" | "family";
export const BLOG_PRODUCTS: Record<BlogProduct, string> = { studio: "Photohawk Studio", family: "Photohawk" };

export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  published: string;
  updated?: string;
  source: string;
  supersededBy?: string;
  relatedGuides: string[];
  editorNote?: string;
  product?: BlogProduct;
  /** An author id from src/content/guides/authors.ts; the byline says "Photohawk" when there's none. */
  author?: string;
  relatedPosts: string[];
  faq: GuideFaq[];
  blocks: GuideBlock[];
  words: number;
  notes: string[];
  /** The hero image under the title (a content image name, src/content/media), with its alt text and caption. */
  hero?: { image: string; alt: string; caption?: string };
  /** The link-preview image's path (1200x630 JPEG), when it isn't the default card for the hero. */
  socialImage?: string;
  socialAlt?: string;
  /** For the event photography pages: which group of the hub it's listed in. */
  group?: string;
}

/** The content images an article shows: its hero, then its figures, in order (names from src/content/media). */
export const postImages = (p: BlogPost): { image: string; alt: string; caption?: string }[] => {
  const figures = (blocks: GuideBlock[]): { image: string; alt: string; caption?: string }[] =>
    blocks.flatMap((b) => (b.type === "figure" ? [{ image: b.image, alt: b.alt, caption: b.caption }] : b.type === "callout" ? figures(b.blocks) : []));
  return [...(p.hero ? [p.hero] : []), ...figures(p.blocks)].filter((x) => !!getMedia(x.image));
}

const one = (fm: FrontMatter, key: string, where: string, optional = false): string | undefined => {
  const v = fm[key];
  if (v === undefined || v === "") {
    if (optional) return undefined;
    throw new Error(`${where}: "${key}" is missing`);
  }
  if (typeof v !== "string") throw new Error(`${where}: "${key}" must be a single value`);
  return v;
};

export function buildPost(path: string, src: string): BlogPost {
  const m = /([\w-]+)\.md$/.exec(path);
  if (!m) throw new Error(`${path}: blog posts live at src/content/blog/<slug>.md`);
  const { data, body } = splitFrontMatter(src);
  const slug = one(data, "slug", path)!;
  if (slug !== m[1]) throw new Error(`${path}: slug "${slug}" doesn't match the file name`);
  const published = one(data, "published", path)!;
  const updated = one(data, "updated", path, true);
  for (const d of [published, updated]) if (d && !/^\d{4}-\d{2}-\d{2}$/.test(d)) throw new Error(`${path}: dates are YYYY-MM-DD`);
  const supersededBy = one(data, "supersededBy", path, true);
  const related = Array.isArray(data.relatedGuides) ? (data.relatedGuides as string[]) : data.relatedGuides ? [data.relatedGuides as string] : [];
  for (const g of [supersededBy, ...related]) if (g && !getGuide(g)) throw new Error(`${path}: no guide "${g}"`);
  const product = one(data, "product", path, true);
  if (product && !(product in BLOG_PRODUCTS)) throw new Error(`${path}: product is studio or family`);
  const author = one(data, "author", path, true);
  if (author && (!findAuthor(author) || findAuthor(author)!.placeholder)) throw new Error(`${path}: no (non-placeholder) author "${author}" in authors.ts`);
  const relatedPosts = Array.isArray(data.relatedPosts) ? (data.relatedPosts as string[]) : data.relatedPosts ? [data.relatedPosts as string] : [];
  const faqRaw = data.faq ?? [];
  if (!Array.isArray(faqRaw) || faqRaw.some((x) => typeof x !== "object")) throw new Error(`${path}: faq must be a list of q/a pairs`);
  const faq = faqRaw as GuideFaq[];
  const blocks = parseGuideBody(body);
  const heroImage = one(data, "heroImage", path, true);
  if (heroImage && !getMedia(heroImage)) throw new Error(`${path}: no content image "${heroImage}" in src/content/media`);
  const heroAlt = one(data, "heroAlt", path, true) ?? getMedia(heroImage)?.alt;
  return {
    slug,
    title: one(data, "title", path)!,
    metaTitle: one(data, "metaTitle", path, true) ?? one(data, "title", path)!,
    description: one(data, "description", path)!,
    published,
    updated,
    source: one(data, "source", path)!,
    supersededBy,
    relatedGuides: related,
    editorNote: one(data, "editorNote", path, true),
    product: product as BlogProduct | undefined,
    author,
    relatedPosts,
    faq,
    blocks,
    words: countWords([...blocksText(blocks), ...faq.flatMap((f) => [f.q, f.a])]),
    notes: htmlComments(src),
    hero: heroImage ? { image: heroImage, alt: heroAlt!, caption: one(data, "heroCaption", path, true) } : undefined,
    socialImage: one(data, "socialImage", path, true),
    socialAlt: one(data, "socialAlt", path, true),
    group: one(data, "group", path, true),
  };
}

const FILES = import.meta.glob("./*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;

/** Every post, newest first. */
export const BLOG_POSTS: BlogPost[] = Object.entries(FILES)
  .map(([p, src]) => buildPost(p, src))
  .sort((a, b) => (a.published === b.published ? a.slug.localeCompare(b.slug) : a.published < b.published ? 1 : -1));

for (const p of BLOG_POSTS)
  for (const r of p.relatedPosts) if (r === p.slug || !BLOG_POSTS.some((x) => x.slug === r)) throw new Error(`${p.slug}: relatedPosts has no post "${r}"`);

export const getPost = (slug: string) => BLOG_POSTS.find((p) => p.slug === slug);

/** The articles written for the blog (not restored from 2023). */
export const isNewArticle = (p: BlogPost) => p.source === "original";

export const blogPath = (slug: string) => `${BLOG_PATH}/${slug}`;

export const BLOG_INDEX_SEO = {
  title: "Blog | Photohawk",
  description: "Articles on sharing and selling event photos with Photohawk Studio, and on finding and sharing your family's photos with the Photohawk app.",
};
